"use client";

import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type FocusEvent,
	type PointerEvent,
	type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../../lib/utils";

/**
 * Gives Streamdown's control buttons the same tooltip chip the message actions use.
 *
 * WHY THIS IS DOM WORK RATHER THAN A PROP. Streamdown renders the copy, download
 * and fullscreen buttons itself. `controls` only switches them on and off,
 * `translations` decides their wording but not how it is shown, and `components`
 * maps markdown elements — these are chrome, not markdown. There is no render slot,
 * so the buttons cannot be wrapped in a `Tooltip` from outside.
 *
 * WHY A PORTAL RATHER THAN `::after`. The first attempt drew the chip as a pseudo
 * element on the button, and it was cut off. There are FIVE clipping ancestors
 * between a control and the page — the message body, the transcript scroller, the
 * chat column, and a Radix ScrollArea viewport, only the first of which is ours —
 * and nothing painted inside the button can escape them. Fixed position at the
 * document root is the only way out, which is what a real tooltip does too.
 */

/** Only the two containers that hold controls, so no ordinary link or button is caught. */
const CONTROL_SELECTOR = [
	"[data-streamdown=table-wrapper] button[title]",
	"[data-streamdown=code-block-actions] button[title]",
].join(",");

/** Clear of the button, matching Radix's `sideOffset={4}` plus the chip's own shadow. */
const GAP = 6;
/** Kept this far from the viewport edge when a chip would otherwise run off it. */
const EDGE = 8;

type Tip = { text: string; anchor: DOMRect };

function adoptTooltips(root: HTMLElement) {
	root.querySelectorAll<HTMLElement>(CONTROL_SELECTOR).forEach((button) => {
		const title = button.getAttribute("title");
		if (!title) {
			return;
		}

		// The copy dropdown's Markdown / CSV / TSV items are titled buttons in this
		// same wrapper, and they already say what they do in their own label. Only
		// the icon controls — a bare glyph, nothing else — take a chip.
		if (!button.querySelector(":scope > svg")) {
			return;
		}

		// Removing `title` is what suppresses the browser's own popup. Leaving it
		// would show the native tooltip a second after ours, one under the other.
		button.removeAttribute("title");
		button.setAttribute("data-tip", title);
		if (!button.getAttribute("aria-label")) {
			button.setAttribute("aria-label", title);
		}
	});
}

/** The chip, positioned against the button it belongs to and clamped to the viewport. */
function TooltipChip({ tip }: { tip: Tip }) {
	const ref = useRef<HTMLDivElement>(null);
	const [style, setStyle] = useState<{ top: number; left: number } | null>(
		null,
	);

	// Layout effect, not effect: the chip is measured and moved before the browser
	// paints, so it never appears at the wrong place first.
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) {
			return;
		}

		const { width, height } = el.getBoundingClientRect();
		// Above by default, which is where Radix puts it. Flipped only when the chip
		// would not actually fit — measured against its own height rather than a
		// guessed threshold, so a short window and a tall one both get it right.
		const below = tip.anchor.top - GAP - height < EDGE;
		const top = below ? tip.anchor.bottom + GAP : tip.anchor.top - GAP - height;

		// Centred on the button, then pulled back inside the viewport. The fullscreen
		// control sits hard against the right edge of a table, where a chip as wide as
		// "View fullscreen" would otherwise hang off the window.
		const centred = tip.anchor.left + tip.anchor.width / 2 - width / 2;
		const left = Math.min(
			Math.max(centred, EDGE),
			window.innerWidth - width - EDGE,
		);

		setStyle({ top, left });
	}, [tip]);

	return createPortal(
		<div
			className={cn(
				// The same chip `TooltipContent` renders, so a control and a message
				// action look identical when hovered.
				"pointer-events-none fixed z-50 whitespace-nowrap rounded-md bg-neutral-600 px-3 py-1.5",
				"text-sm font-normal text-neutral-50 shadow-md dark:bg-neutral-950",
				style ? "opacity-100" : "opacity-0",
			)}
			ref={ref}
			role="tooltip"
			// Off-screen until measured, so the first paint cannot flash at 0,0.
			style={{ top: style?.top ?? -9999, left: style?.left ?? -9999 }}
		>
			{tip.text}
		</div>,
		document.body,
	);
}

export function MarkdownControlTooltips({ children }: { children: ReactNode }) {
	const ref = useRef<HTMLDivElement>(null);
	const [tip, setTip] = useState<Tip | null>(null);

	useEffect(() => {
		const root = ref.current;
		if (!root) {
			return;
		}

		adoptTooltips(root);

		// An answer streams in, so the controls do not exist on mount — a table
		// arrives mid-run and brings its own. Watching `title` as well as the tree
		// covers streamdown re-setting it on a re-render. No loop: the handler only
		// acts while a `title` is present, and removing it is the last thing it does.
		const observer = new MutationObserver(() => adoptTooltips(root));
		observer.observe(root, {
			childList: true,
			subtree: true,
			attributeFilter: ["title"],
		});

		return () => observer.disconnect();
	}, []);

	// Delegated, so a control that streams in later needs no listener of its own.
	const show = useCallback((event: PointerEvent | FocusEvent) => {
		const control = (event.target as HTMLElement | null)?.closest?.(
			"[data-tip]",
		);
		if (!(control instanceof HTMLElement)) {
			return;
		}
		setTip({
			text: control.getAttribute("data-tip") ?? "",
			anchor: control.getBoundingClientRect(),
		});
	}, []);

	const hide = useCallback((event: PointerEvent | FocusEvent) => {
		const control = (event.target as HTMLElement | null)?.closest?.(
			"[data-tip]",
		);
		if (!control) {
			return;
		}
		const next = event.relatedTarget as Node | null;
		if (next && control.contains(next)) {
			return;
		}
		setTip(null);
	}, []);

	return (
		// `contents`, so this wrapper adds no box of its own and the markdown keeps the
		// layout it had before. The observer still sees the subtree beneath it.
		<div
			className="contents"
			onBlur={hide}
			onFocus={show}
			onPointerOut={hide}
			onPointerOver={show}
			ref={ref}
		>
			{children}
			{tip && <TooltipChip tip={tip} />}
		</div>
	);
}
